import { isTranscriptNeedsCoachReview } from "@/lib/transcriptStatus";

export const COACH_HEALTH_EVIDENCE_SOURCE_TYPES = Object.freeze([
  "coach_philosophy",
  "completed_coach_call",
  "coach_transcript_session_record",
  "coach_first_take",
  "coach_reviewable_transcript",
  "coaching_continuity_moment",
] as const);

export type CoachHealthEvidenceSourceType =
  (typeof COACH_HEALTH_EVIDENCE_SOURCE_TYPES)[number];

export const COACH_HEALTH_RECORDED_CONTEXT_SOURCE_TYPES = Object.freeze([
  "coach_transcript_session_record",
  "coach_first_take",
  "coach_reviewable_transcript",
  "coaching_continuity_moment",
] as const);

export type CoachHealthRecordedContextSourceType =
  (typeof COACH_HEALTH_RECORDED_CONTEXT_SOURCE_TYPES)[number];

export type CoachHealthEvidenceDimension = "continuity";

export type CoachHealthEvidenceContribution = "context_only" | "evidence";

export type CoachHealthEvidenceRouteBuilder =
  | "coach_identity"
  | "coach_meeting"
  | "coach_post_meeting"
  | "coach_reflect"
  | "coach_transcript_review"
  | "coach_session_record_destination";

export interface CoachHealthEvidenceSourceDefinition {
  readonly sourceType: CoachHealthEvidenceSourceType;
  readonly tableOrView: string;
  readonly acceptedPredicate: string;
  readonly authorizationRule: string;
  readonly evidenceTimestampRule: string;
  readonly canonicalIdRule: string;
  readonly eventKeyRule: string;
  readonly routeBuilder: CoachHealthEvidenceRouteBuilder;
  readonly publicLabel: string;
  readonly publicErrorCode: string;
  readonly allowedDimensions: readonly CoachHealthEvidenceDimension[];
  readonly contribution: CoachHealthEvidenceContribution;
  readonly admitsLeaderPerspective: false;
}

function freezeDefinition(
  definition: CoachHealthEvidenceSourceDefinition,
): Readonly<CoachHealthEvidenceSourceDefinition> {
  return Object.freeze({
    ...definition,
    allowedDimensions: Object.freeze([...definition.allowedDimensions]),
  });
}

const COACH_HEALTH_EVIDENCE_SOURCE_REGISTRY = Object.freeze({
  coach_philosophy: freezeDefinition({
    sourceType: "coach_philosophy",
    tableOrView: "coach_lens",
    acceptedPredicate:
      "is_active IS TRUE AND nullif(btrim(coach_lens_json ->> 'philosophy'), '') IS NOT NULL",
    authorizationRule: "coach_lens.coach_id = authenticated_coach_id",
    evidenceTimestampRule: "coach_lens_updated_at ?? updated_at",
    canonicalIdRule: "coach_lens.id",
    eventKeyRule: "coach_lens:{lensId}",
    routeBuilder: "coach_identity",
    publicLabel: "Coach philosophy",
    publicErrorCode: "coach_health_source_coach_philosophy_unavailable",
    allowedDimensions: [],
    contribution: "context_only",
    admitsLeaderPerspective: false,
  }),
  completed_coach_call: freezeDefinition({
    sourceType: "completed_coach_call",
    tableOrView: "coach_calls + coach_call_participants",
    acceptedPredicate: "coach_calls.status = 'completed'",
    authorizationRule:
      "exact authenticated coach + exact participant leader + current authorized relationship",
    evidenceTimestampRule: "coach_calls.scheduled_end",
    canonicalIdRule: "coach_calls.id",
    eventKeyRule: "coach_call:{callId}",
    routeBuilder: "coach_meeting",
    publicLabel: "Completed coaching session",
    publicErrorCode: "coach_health_source_completed_coach_call_unavailable",
    allowedDimensions: ["continuity"],
    contribution: "evidence",
    admitsLeaderPerspective: false,
  }),
  coach_transcript_session_record: freezeDefinition({
    sourceType: "coach_transcript_session_record",
    tableOrView: "coach_leader_session_records",
    acceptedPredicate:
      "source_type = 'transcript_review' AND transcript_upload_id IS NOT NULL AND linked coach_call.status = 'completed'",
    authorizationRule:
      "exact authenticated coach + exact leader + current authorized relationship",
    evidenceTimestampRule:
      "coach_calls.scheduled_end <= coach_leader_session_records.updated_at",
    canonicalIdRule: "coach_leader_session_records.id",
    eventKeyRule: "coach_call:{verifiedCompletedCallId}",
    routeBuilder: "coach_post_meeting",
    publicLabel: "Coach-reviewed session record",
    publicErrorCode: "coach_health_source_coach_session_record_unavailable",
    allowedDimensions: ["continuity"],
    contribution: "evidence",
    admitsLeaderPerspective: false,
  }),
  coach_first_take: freezeDefinition({
    sourceType: "coach_first_take",
    tableOrView: "coach_leader_session_records",
    acceptedPredicate:
      "source_type = 'coach_first_take' AND normalizeCoachFirstTakeRecord succeeds AND at least one normalized non-empty coach-authored answer or coach_note exists AND embedded coach_call.status = 'completed'",
    authorizationRule:
      "embedded call exact authenticated coach + exact participant leader + current authorized relationship",
    evidenceTimestampRule:
      "coach_calls.scheduled_end <= coach_leader_session_records.updated_at",
    canonicalIdRule: "coach_leader_session_records.id",
    eventKeyRule: "coach_call:{embeddedCallId}",
    routeBuilder: "coach_reflect",
    publicLabel: "Coach first take",
    publicErrorCode: "coach_health_source_coach_first_take_unavailable",
    allowedDimensions: ["continuity"],
    contribution: "evidence",
    admitsLeaderPerspective: false,
  }),
  coach_reviewable_transcript: freezeDefinition({
    sourceType: "coach_reviewable_transcript",
    tableOrView: "transcript_uploads",
    acceptedPredicate:
      "isTranscriptNeedsCoachReview(status, coach_verified) AND linked coach_call.status = 'completed'",
    authorizationRule:
      "exact authenticated coach-session lineage + exact leader + current authorized relationship",
    evidenceTimestampRule: "coach_calls.scheduled_end <= transcript_uploads.updated_at",
    canonicalIdRule: "transcript_uploads.id",
    eventKeyRule: "coach_call:{verifiedCompletedCallId}",
    routeBuilder: "coach_transcript_review",
    publicLabel: "Transcript ready for coach review",
    publicErrorCode:
      "coach_health_source_coach_reviewable_transcript_unavailable",
    allowedDimensions: ["continuity"],
    contribution: "evidence",
    admitsLeaderPerspective: false,
  }),
  coaching_continuity_moment: freezeDefinition({
    sourceType: "coaching_continuity_moment",
    tableOrView: "continuity_moments",
    acceptedPredicate:
      "kind = 'coaching' AND source_table = 'coach_leader_session_records' AND source IN ('transcript_review_completed', 'coaching_note_finalized') AND source_id IS NOT NULL AND linked coach_call.status = 'completed'",
    authorizationRule:
      "exact authenticated coach + exact relationship leader + current authorized relationship + approved source-record lineage",
    evidenceTimestampRule: "coach_calls.scheduled_end <= continuity_moments.occurred_at",
    canonicalIdRule: "continuity_moments.id",
    eventKeyRule: "coach_call:{verifiedCompletedCallId}",
    routeBuilder: "coach_session_record_destination",
    publicLabel: "Coaching continuity",
    publicErrorCode: "coach_health_source_coaching_continuity_unavailable",
    allowedDimensions: ["continuity"],
    contribution: "evidence",
    admitsLeaderPerspective: false,
  }),
} satisfies Readonly<
  Record<CoachHealthEvidenceSourceType, CoachHealthEvidenceSourceDefinition>
>);

const APPROVED_CONTINUITY_MOMENT_SOURCES = Object.freeze([
  "transcript_review_completed",
  "coaching_note_finalized",
] as const);

export function isCoachHealthEvidenceSourceType(
  value: unknown,
): value is CoachHealthEvidenceSourceType {
  return (
    typeof value === "string" &&
    COACH_HEALTH_EVIDENCE_SOURCE_TYPES.some((sourceType) => sourceType === value)
  );
}

export function isCoachHealthRecordedContextSourceType(
  value: unknown,
): value is CoachHealthRecordedContextSourceType {
  return typeof value === "string" &&
    COACH_HEALTH_RECORDED_CONTEXT_SOURCE_TYPES.some(
      (sourceType) => sourceType === value,
    );
}

export function getCoachHealthEvidenceSourceDefinition(
  value: unknown,
): Readonly<CoachHealthEvidenceSourceDefinition> | null {
  if (!isCoachHealthEvidenceSourceType(value)) {
    return null;
  }

  return COACH_HEALTH_EVIDENCE_SOURCE_REGISTRY[value];
}

export function isCoachHealthTranscriptReviewable(
  status: string | null | undefined,
  coachVerified: boolean | null | undefined,
): boolean {
  return isTranscriptNeedsCoachReview(status, coachVerified);
}

export function isApprovedCoachingContinuityMomentSource(
  sourceTable: string | null | undefined,
  source: string | null | undefined,
): boolean {
  return (
    sourceTable === "coach_leader_session_records" &&
    typeof source === "string" &&
    APPROVED_CONTINUITY_MOMENT_SOURCES.some(
      (approvedSource) => approvedSource === source,
    )
  );
}
